import { api } from "@/lib/api";
import type {
  ApiResponse,
  CourseBundle,
  PaginatedResponse,
} from "@learn-me/types";

interface BundleListItem {
  id: string;
  name: string;
  slug: string;
  shortDescription?: string;
  thumbnailUrl?: string;
  hasDiscount: boolean;
  price?: number | null;
  originalPrice?: number | null;
  currency: string;
  isPublished: boolean;
  position: number;
  coursesCount: number;
}

interface CreateBundleData {
  name: string;
  description?: string;
  shortDescription?: string;
  thumbnailUrl?: string;
  hasDiscount?: boolean;
  price?: number;
  originalPrice?: number;
  currency?: string;
  isPublished?: boolean;
  position?: number;
  courseIds?: string[];
}

export const bundlesApi = {
  // Listar paquetes publicados (público)
  async list(): Promise<CourseBundle[]> {
    const response = await api.get<ApiResponse<CourseBundle[]>>("/bundles");
    return response.data.data!;
  },

  // ADMIN: Listar todos los paquetes
  async listAll(
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<PaginatedResponse<BundleListItem>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.set("search", search);

    const response = await api.get<
      ApiResponse<PaginatedResponse<BundleListItem>>
    >(`/bundles/all?${params}`);
    return response.data.data!;
  },

  // Obtener paquete por slug (público)
  async getBySlug(slug: string): Promise<CourseBundle> {
    const response = await api.get<ApiResponse<CourseBundle>>(
      `/bundles/${slug}`,
    );
    return response.data.data!;
  },

  // ADMIN: Obtener paquete por ID para edición
  async getById(id: string): Promise<CourseBundle> {
    const response = await api.get<ApiResponse<CourseBundle>>(
      `/bundles/${id}/edit`,
    );
    return response.data.data!;
  },

  // ADMIN: Crear paquete
  async create(data: CreateBundleData): Promise<CourseBundle> {
    const response = await api.post<ApiResponse<CourseBundle>>(
      "/bundles",
      data,
    );
    return response.data.data!;
  },

  // ADMIN: Actualizar paquete
  async update(
    id: string,
    data:
      | Partial<Omit<CreateBundleData, "courseIds">>
      | Record<string, unknown>,
  ): Promise<CourseBundle> {
    const response = await api.put<ApiResponse<CourseBundle>>(
      `/bundles/${id}`,
      data,
    );
    return response.data.data!;
  },

  // ADMIN: Actualizar cursos del paquete
  async updateCourses(id: string, courseIds: string[]): Promise<CourseBundle> {
    const response = await api.put<ApiResponse<CourseBundle>>(
      `/bundles/${id}/courses`,
      { courseIds },
    );
    return response.data.data!;
  },

  // ADMIN: Eliminar paquete
  async delete(id: string): Promise<void> {
    await api.delete(`/bundles/${id}`);
  },

  // ADMIN: Duplicar paquete
  async duplicate(id: string): Promise<CourseBundle> {
    const response = await api.post<ApiResponse<CourseBundle>>(
      `/admin/bundles/${id}/duplicate`,
    );
    return response.data.data!;
  },
};
